<template>
  <div class="flex mb-2">
    <div class="flex-0 flex align-items-center justify-content-center">
      <div class="px-0 py-2">
        <h2 class="m-0 p-0 font-light text-600">{{ pageTitle }}</h2>
      </div>
    </div>
    <div class="flex-1 flex align-items-center gap-2 justify-content-end">
      <TreeSelect
        v-model="selectionKeys"
        :options="treeOptions"
        size="small"
        filter
        filterMode="lenient"
        filterPlaceholder="Search companies or employees"
        selectionMode="checkbox"
        :disabled="selectionLoading || isCalendarLoading"
        placeholder="All Employees"
        class="w-full md:w-25rem"
      >
        <template #value>
          <span>{{ treeSelectionLabel }}</span>
        </template>
      </TreeSelect>
      <Button
        icon="pi pi-search"
        size="small"
        aria-label="Search"
        v-tooltip.top="'Search'"
        :loading="isCalendarLoading"
        :disabled="selectionLoading || !routeFilters.valid"
        @click="applyEmployeeSelection"
      />
    </div>
  </div>

  <div v-if="routeError" class="card">
    <Message severity="error" :closable="false">{{ routeError }}</Message>
  </div>
  <div v-else-if="!initialized" class="card text-300">Loading calendar…</div>
  <div v-else class="card relative">
    <FullCalendar ref="calendarRef" :options="calendarOptions" />
    <div
      v-if="isCalendarLoading"
      class="calendar-loading-overlay flex flex-column align-items-center justify-content-center"
      role="status"
      aria-live="polite"
      :aria-label="`Loading ${recordLabel.toLowerCase()} calendar`"
    >
      <ProgressSpinner strokeWidth="4" class="calendar-loading-spinner" />
      <span class="mt-2 text-sm font-bold text-100">Loading calendar…</span>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendar from '@fullcalendar/vue3';
import Button from 'primevue/button';
import Message from 'primevue/message';
import ProgressSpinner from 'primevue/progressspinner';
import TreeSelect from 'primevue/treeselect';
import Tooltip from 'primevue/tooltip';
import { useToast } from 'primevue/usetoast';
import { useScheduleCalendar } from '@/composables/api/useScheduleCalendar';
import { useEmployeeTreeSelection } from '@/composables/useEmployeeTreeSelection';
import {
  buildCalendarRoute,
  parseCalendarRouteFilters
} from '@/composables/useCalendarRouteFilters';

const props = defineProps({
  calendarType: {
    type: String,
    default: 'leaves',
    validator: value => ['leaves', 'covers'].includes(value)
  }
});

const route = useRoute();
const router = useRouter();
const toast = useToast();
const { fetchCalendarRecords } = useScheduleCalendar();
const {
  selectionKeys,
  treeOptions,
  eligibleEmployees,
  selectedEmployeeIds,
  selectedEmployeeFirstNames,
  selectionLoading,
  setSelectedEmployeeIds,
  ensureSelectionData
} = useEmployeeTreeSelection();

const initialized = ref(false);
const initializationError = ref(null);
const isCalendarLoading = ref(false);
const appliedEmployeeIds = ref([]);
const calendarRef = ref(null);

const routeFilters = computed(() => parseCalendarRouteFilters(route.params));
const routeError = computed(() => routeFilters.value.valid
  ? initializationError.value
  : routeFilters.value.error);
const recordLabel = computed(() => props.calendarType === 'covers' ? 'Cover' : 'Leave');

const appliedEmployeeNames = computed(() => {
  const selectedIds = new Set(appliedEmployeeIds.value);
  return eligibleEmployees.value
    .filter(employee => selectedIds.has(employee.employee_id))
    .map(employee => employee.employee_name);
});

const pageTitle = computed(() => {
  if (appliedEmployeeNames.value.length === 0) return `All ${recordLabel.value} Calendar`;
  if (appliedEmployeeNames.value.length === 1) return `${appliedEmployeeNames.value[0]} ${recordLabel.value} Calendar`;
  return `${appliedEmployeeNames.value.length} Employees ${recordLabel.value} Calendar`;
});

const treeSelectionLabel = computed(() => {
  if (selectedEmployeeIds.value.length === 0) return 'All Employees';
  if (selectedEmployeeIds.value.length === 1) {
    return eligibleEmployees.value.find(
      employee => employee.employee_id === selectedEmployeeIds.value[0]
    )?.employee_name || '1 Employee selected';
  }
  return selectedEmployeeFirstNames.value.join(', ');
});

const calendarOptions = ref({
  plugins: [dayGridPlugin],
  initialView: 'dayGridMonth',
  initialDate: new Date(),
  events: loadCalendarEvents,
  headerToolbar: {
    left: 'prev,next today',
    center: 'title',
    right: ''
  },
  displayEventTime: false,
  eventDisplay: 'block',
  dayMaxEvents: false,
  lazyFetching: true,
  datesSet: handleVisibleDatesChanged,
  loading: loading => {
    isCalendarLoading.value = loading;
  },
  eventDidMount: info => {
    const tooltip = `
      Time: ${info.event.extendedProps.startTime} - ${info.event.extendedProps.endTime}
      ${info.event.title}
      Reason: ${getEventReasonLabel(info.event.extendedProps.eventReason)}
    `;
    info.el.setAttribute('title', tooltip);
  },
  eventClick: info => {
    router.push({
      name: 'EditRoster',
      params: { rosterIds: String(info.event.id) },
      query: { returnTo: route.fullPath }
    });
  }
});

const SHIFT_REASONS = [
  { label: 'Undefined', value: 'UNDEFINED' },
  { label: 'Death in Family', value: 'DEATH_IN_FAMILY_LEAVE' },
  { label: 'Casual Leave', value: 'CASUAL_LEAVE' },
  { label: 'Emergency Leave', value: 'EMERGENCY_LEAVE' },
  { label: 'Wedding Leave', value: 'WEDDING_LEAVE' },
  { label: 'Birthday Leave', value: 'BIRTHDAY_LEAVE' },
  { label: 'Uninformed Leave', value: 'UNINFORMED_LEAVE' },
  { label: 'Road Accident', value: 'ROAD_ACCIDENT_LEAVE' },
  { label: 'National Holiday', value: 'NATIONAL_HOLIDAY_LEAVE' },
  { label: 'Sick Leave', value: 'SICK_LEAVE' },
  { label: 'Put on Hold', value: 'PUT_ONHOLD_LEAVE' }
];

function formatCalendarDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatCalendarMonth(date) {
  return formatCalendarDate(date).slice(0, 7);
}

function getEmployeeColor(employeeId) {
  const colors = [
    { bg: '#1E3A5F', text: '#FFFFFF' },
    { bg: '#4C1D95', text: '#FFFFFF' },
    { bg: '#14532D', text: '#FFFFFF' },
    { bg: '#7C2D12', text: '#FFFFFF' },
    { bg: '#164E63', text: '#FFFFFF' },
    { bg: '#3F6212', text: '#FFFFFF' },
    { bg: '#831843', text: '#FFFFFF' },
    { bg: '#44403C', text: '#FFFFFF' },
    { bg: '#312E81', text: '#FFFFFF' },
    { bg: '#713F12', text: '#FFFFFF' }
  ];
  return colors[employeeId % colors.length];
}

function mapCalendarEvent(roster) {
  const colors = getEmployeeColor(roster.employee_id);
  const startTime = new Date(roster.roster_start_unix).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC'
  });
  const endTime = new Date(roster.roster_end_unix).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'UTC'
  });
  const startDate = roster.roster_start.split(' ')[0];
  const endDate = roster.roster_end.split(' ')[0];
  const eventEnd = startDate === endDate ? roster.roster_end : `${startDate} 23:59:59`;

  return {
    id: String(roster.roster_id),
    title: `${roster.employee_name} (${roster.employee_company_name})`,
    allDay: false,
    start: roster.roster_start,
    end: eventEnd,
    backgroundColor: colors.bg,
    textColor: colors.text,
    borderColor: colors.bg,
    extendedProps: {
      department: roster.employee_department,
      status: roster.employee_status,
      company: roster.employee_company_name,
      startTime,
      endTime,
      employeeId: roster.employee_id,
      eventReason: roster.shift_reason
    }
  };
}

async function loadCalendarEvents(fetchInfo, successCallback, failureCallback) {
  try {
    const rows = await fetchCalendarRecords({
      calendarType: props.calendarType,
      employeeIds: [...appliedEmployeeIds.value],
      startDate: formatCalendarDate(fetchInfo.start),
      endDateExclusive: formatCalendarDate(fetchInfo.end)
    });
    successCallback(rows.map(mapCalendarEvent));
  } catch (error) {
    const message = error.response?.data?.error || error.message || `Failed to load ${recordLabel.value.toLowerCase()} calendar.`;
    toast.add({ severity: 'error', summary: 'Calendar error', detail: message, life: 12000, group: 'br' });
    failureCallback(error);
  }
}

function handleVisibleDatesChanged(dateInfo) {
  if (!initialized.value || !routeFilters.value.valid) return;

  const visibleMonth = formatCalendarMonth(dateInfo.view.currentStart);
  if (visibleMonth === routeFilters.value.month) return;

  router.replace(buildCalendarRoute({
    calendarType: props.calendarType,
    employeeIds: appliedEmployeeIds.value,
    month: visibleMonth
  }));
}

function applyEmployeeSelection() {
  const targetRoute = buildCalendarRoute({
    calendarType: props.calendarType,
    employeeIds: selectedEmployeeIds.value,
    month: routeFilters.value.month
  });
  const targetPath = router.resolve(targetRoute).fullPath;
  if (targetPath === route.fullPath) {
    calendarRef.value?.getApi().refetchEvents();
  } else {
    router.push(targetRoute);
  }
}

function getEventReasonLabel(value) {
  return SHIFT_REASONS.find(reason => reason.value === value)?.label || 'Unknown Reason';
}

watch(() => route.fullPath, async () => {
  if (!initialized.value) return;
  const parsed = parseCalendarRouteFilters(route.params);
  if (!parsed.valid) return;

  const employeeSelectionChanged = parsed.employeeIds.join(',') !== appliedEmployeeIds.value.join(',');
  appliedEmployeeIds.value = [...parsed.employeeIds];
  setSelectedEmployeeIds(parsed.employeeIds);

  await nextTick();
  const calendarApi = calendarRef.value?.getApi();
  if (!calendarApi) return;

  if (formatCalendarMonth(calendarApi.getDate()) !== parsed.month) {
    calendarApi.gotoDate(parsed.initialDate);
  } else if (employeeSelectionChanged) {
    calendarApi.refetchEvents();
  }
});

onMounted(async () => {
  if (!routeFilters.value.valid) {
    initialized.value = true;
    return;
  }

  try {
    await ensureSelectionData();
    appliedEmployeeIds.value = [...routeFilters.value.employeeIds];
    const missingEmployeeIds = setSelectedEmployeeIds(routeFilters.value.employeeIds);
    if (missingEmployeeIds.length) {
      initializationError.value = `Employee ${missingEmployeeIds.join(', ')} could not be found.`;
      return;
    }
    calendarOptions.value.initialDate = routeFilters.value.initialDate;
  } catch (error) {
    initializationError.value = error.response?.data?.error || error.message || 'Failed to load calendar filters.';
  } finally {
    initialized.value = true;
  }
});

const vTooltip = Tooltip;
</script>

<style scoped>
:deep(.fc) {
  background: var(--surface-card);
  border-radius: var(--border-radius);
  padding: 1rem;
}

:deep(.fc-event) {
  cursor: pointer;
  padding: 1px 3px !important;
  margin: 2px 4px !important;
}

:deep(.fc-toolbar-title) {
  font-size: 1rem !important;
}

:deep(.fc-daygrid-event) {
  font-size: 0.7rem !important;
  font-weight: 300 !important;
  border-radius: 2px !important;
  border: none !important;
  background-clip: padding-box;
}

:deep(.fc-daygrid-day-top) {
  font-size: 0.8rem !important;
  font-weight: 300 !important;
}

:deep(.fc-daygrid-event-harness) {
  margin: 1px 0 !important;
}

:deep(.fc-daygrid-event-dot) {
  display: none !important;
}

:deep(.fc-event-time),
:deep(.fc-event-title) {
  padding: 0 1px;
  font-weight: 600 !important;
}

:deep(.fc-event:hover, .fc-event:focus, .fc-event:active) {
  background-color: #60a5fa !important;
}

.calendar-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 5;
  border-radius: var(--border-radius);
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(1px);
}

.calendar-loading-spinner {
  width: 3rem;
  height: 3rem;
}
</style>
